// ui/settingsModal.js
// Settings modal for game configuration

import { gameState } from '../game/gameState.js';
import { refreshCurrentSkill } from '../core/routeSetup.js';
import { forceRefreshInventory } from './inventoryUI.js';
import { toastSuccess, toastError } from './toast.js';
import logger from '../utils/logger.js';

let modalElement = null;

/**
 * Create the settings modal HTML
 */
function createModalHTML() {
    const { virtualLevelsEnabled, autoSave, showStatComparison, inventoryPanelLeft } = gameState.settings;

    return `
        <div class="modal-overlay" id="settings-modal-overlay">
            <div class="modal-content settings-modal">
                <div class="modal-header">
                    <h2>Settings</h2>
                    <button class="modal-close" onclick="window.settingsModal.close()">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="settings-section">
                        <h3>Gameplay</h3>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Virtual Levels</span>
                                <span class="label-hint">Track XP progress beyond level 99 (up to 120)</span>
                            </div>
                            <label class="toggle-switch">
                                <input type="checkbox" id="setting-virtual-levels"
                                       ${virtualLevelsEnabled ? 'checked' : ''}
                                       onchange="window.settingsModal.toggleSetting('virtualLevelsEnabled', this.checked)">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Auto Save</span>
                                <span class="label-hint">Automatically save every 30 seconds</span>
                            </div>
                            <label class="toggle-switch">
                                <input type="checkbox" id="setting-auto-save"
                                       ${autoSave ? 'checked' : ''}
                                       onchange="window.settingsModal.toggleSetting('autoSave', this.checked)">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Stat Comparison</span>
                                <span class="label-hint">Show green/red stats when comparing equipment</span>
                            </div>
                            <label class="toggle-switch">
                                <input type="checkbox" id="setting-stat-comparison"
                                       ${showStatComparison !== false ? 'checked' : ''}
                                       onchange="window.settingsModal.toggleSetting('showStatComparison', this.checked)">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Inventory Panel Left</span>
                                <span class="label-hint">Move inventory/equipment panel to the left side</span>
                            </div>
                            <label class="toggle-switch">
                                <input type="checkbox" id="setting-inventory-left"
                                       ${inventoryPanelLeft ? 'checked' : ''}
                                       onchange="window.settingsModal.toggleSetting('inventoryPanelLeft', this.checked)">
                                <span class="toggle-slider"></span>
                            </label>
                        </div>
                    </div>
                    <div class="settings-section">
                        <h3>Save Management</h3>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Export Save</span>
                                <span class="label-hint">Download your save as a file</span>
                            </div>
                            <button class="btn-settings" onclick="window.settingsModal.exportSave()">
                                Export
                            </button>
                        </div>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Import Save</span>
                                <span class="label-hint">Load a previously exported save file</span>
                            </div>
                            <button class="btn-settings" onclick="window.settingsModal.importSave()">
                                Import
                            </button>
                        </div>
                    </div>
                    <div class="settings-section settings-danger">
                        <h3>Danger Zone</h3>
                        <div class="settings-row">
                            <div class="settings-label">
                                <span class="label-text">Delete Save</span>
                                <span class="label-hint">Permanently delete all progress and start fresh</span>
                            </div>
                            <button class="btn-danger" id="delete-save-btn" onclick="window.settingsModal.deleteSave()">
                                Delete Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Open the settings modal
 */
export function openSettingsModal() {
    // Remove existing modal if any
    closeSettingsModal();

    // Create and append modal
    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = createModalHTML();
    modalElement = modalContainer.firstElementChild;
    document.body.appendChild(modalElement);

    // Close on overlay click
    modalElement.addEventListener('click', (e) => {
        if (e.target === modalElement) {
            closeSettingsModal();
        }
    });

    // Close on Escape key
    document.addEventListener('keydown', handleEscapeKey);

    logger.debug('[SETTINGS] Modal opened');
}

/**
 * Close the settings modal
 */
export function closeSettingsModal() {
    if (modalElement) {
        modalElement.remove();
        modalElement = null;
        document.removeEventListener('keydown', handleEscapeKey);
        logger.debug('[SETTINGS] Modal closed');
    }
}

/**
 * Handle Escape key to close modal
 */
function handleEscapeKey(e) {
    if (e.key === 'Escape') {
        closeSettingsModal();
    }
}

/**
 * Save game state to localStorage
 * (Inline save to avoid circular dependency with main.js)
 */
function saveSettings() {
    try {
        const saveData = gameState.export();
        localStorage.setItem('idleRealms_save', JSON.stringify(saveData));
        logger.debug('[SETTINGS] Settings saved');
    } catch (error) {
        logger.error('[SETTINGS] Failed to save settings:', error);
    }
}

/**
 * Delete save data and restart fresh
 */
export function deleteSave() {
    const btn = document.getElementById('delete-save-btn');

    // First click - show confirmation
    if (!btn.dataset.confirm) {
        btn.dataset.confirm = 'true';
        btn.textContent = 'Click again to confirm';
        btn.classList.add('btn-danger-confirm');

        // Reset after 3 seconds if not confirmed
        setTimeout(() => {
            if (btn) {
                btn.dataset.confirm = '';
                btn.textContent = 'Delete Save';
                btn.classList.remove('btn-danger-confirm');
            }
        }, 3000);
        return;
    }

    // Second click - actually delete
    logger.debug('[SETTINGS] Deleting save data...');

    // Set flag to prevent any further saves
    gameState._deletingSave = true;

    // Show confirmation before reload
    btn.textContent = 'Deleted!';
    btn.classList.remove('btn-danger-confirm');
    btn.classList.add('btn-danger-success');
    btn.disabled = true;

    // Brief delay so user sees confirmation, then delete and reload
    setTimeout(() => {
        // Clear save data right before reload to avoid race conditions with auto-save
        localStorage.removeItem('idleRealms_save');
        localStorage.removeItem('idleRealms_save_backup');

        // Also clear any tab preferences
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('idleRealms_tab_')) {
                localStorage.removeItem(key);
            }
        });

        // Force hard reload
        window.location.reload(true);
    }, 800);
}

/**
 * Export save data as a downloadable JSON file
 */
export function exportSave() {
    try {
        // Save current state first
        saveSettings();

        const saveData = localStorage.getItem('idleRealms_save');
        if (!saveData) {
            toastError('No save data found!');
            return;
        }

        const blob = new Blob([saveData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const date = new Date().toISOString().slice(0, 10);
        const filename = `idlerealms-save-${date}.json`;

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        toastSuccess('Save exported!');
        logger.debug('[SETTINGS] Save exported as', filename);
    } catch (error) {
        toastError('Failed to export save!');
        logger.error('[SETTINGS] Export failed:', error);
    }
}

/**
 * Import save data from a JSON file
 */
export function importSave() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';

    input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target.result);

                // Basic validation
                if (!data || !data.player) {
                    toastError('Invalid save file!');
                    return;
                }

                // Prevent auto-save from overwriting
                gameState._deletingSave = true;

                // Back up current save before overwriting so user can recover if import is bad
                const currentSave = localStorage.getItem('idleRealms_save');
                if (currentSave) {
                    localStorage.setItem('idleRealms_save_backup', currentSave);
                }

                // Write imported data to primary save and reload
                localStorage.setItem('idleRealms_save', JSON.stringify(data));
                toastSuccess('Save imported! Reloading...');

                setTimeout(() => {
                    window.location.reload(true);
                }, 800);
            } catch (error) {
                toastError('Invalid save file!');
                logger.error('[SETTINGS] Import failed:', error);
            }
        };
        reader.readAsText(file);
    });

    input.click();
}

/**
 * Toggle a setting value
 */
export function toggleSetting(settingKey, value) {
    gameState.settings[settingKey] = value;
    logger.debug(`[SETTINGS] ${settingKey} = ${value}`);

    // Save immediately so settings persist across refreshes
    saveSettings();

    // Refresh skill display when virtual levels setting changes
    if (settingKey === 'virtualLevelsEnabled') {
        refreshCurrentSkill();
    }

    // Refresh inventory tooltips when stat comparison setting changes
    if (settingKey === 'showStatComparison') {
        forceRefreshInventory();
    }

    // Toggle inventory panel position
    if (settingKey === 'inventoryPanelLeft') {
        const gameMain = document.getElementById('game-main');
        if (gameMain) {
            gameMain.classList.toggle('inventory-left', value);
        }
    }
}

// Expose to window for onclick handlers
window.settingsModal = {
    open: openSettingsModal,
    close: closeSettingsModal,
    toggleSetting,
    deleteSave,
    exportSave,
    importSave,
};

export default {
    openSettingsModal,
    closeSettingsModal,
    toggleSetting,
};
